// Reusable product-photo attachment widget.
// Used by Diamond Verification and Colour Stone Verification.
// Requires three endpoints under `endpointBase`:
//   POST   {endpointBase}/photos                  body: { url, is_primary? }
//   DELETE {endpointBase}/photos/{idx}
//   PATCH  {endpointBase}/photos/{idx}/primary
// All photos are uploaded first via POST /api/upload (Cloudflare R2).

import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Camera, ImagePlus, Star, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('authToken');

const MAX_PHOTOS_DEFAULT = 3;

// Client-side resize so we don't ship huge originals through the proxy
const resizeImage = (file, maxDim = 1600, quality = 0.85) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(b => b ? resolve(b) : reject(new Error('Resize failed')), 'image/jpeg', quality);
    };
    img.onerror = reject;
    img.src = e.target.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function TagPhotos({
  recordId,
  endpointBase,
  photos,
  setPhotos,
  onAutoSave,           // optional: called when recordId is null so DV can save Step 1 first
  maxPhotos = MAX_PHOTOS_DEFAULT,
  title = 'Product Photos',
  emptyHint = 'Attach at least one photo for reference.',
  disabled = false,
  testidPrefix = 'tag-photos',
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const photoSrc = (url) => url.startsWith('http') ? url : `${API}${url}`;

  const handleFile = useCallback(async (file) => {
    if (!file || disabled) return;
    setError('');
    if (!file.type.startsWith('image/')) { setError('Please select an image file.'); return; }
    if (file.size > 15 * 1024 * 1024)     { setError('Image too large (max 15 MB).'); return; }
    if (photos.length >= maxPhotos)        { setError(`Maximum ${maxPhotos} photos.`); return; }

    setUploading(true);
    try {
      let id = recordId;
      if (!id && onAutoSave) id = await onAutoSave();
      if (!id) { setError('Save the record first before attaching photos.'); return; }

      const blob = await resizeImage(file);
      const fd = new FormData();
      fd.append('file', blob, 'tag-photo.jpg');

      const upRes = await axios.post(`${API}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const url = upRes.data.url;

      const attachRes = await axios.post(
        `${API}${endpointBase}/${id}/photos`,
        { url },
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      setPhotos(prev => [...prev, attachRes.data.photo]);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [photos.length, recordId, endpointBase, onAutoSave, setPhotos, maxPhotos, disabled]);

  const handleRemove = useCallback(async (idx) => {
    if (!recordId || disabled) return;
    try {
      await axios.delete(`${API}${endpointBase}/${recordId}/photos/${idx}`,
        { headers: { Authorization: `Bearer ${token()}` } });
      setPhotos(prev => {
        const next = prev.filter((_, i) => i !== idx);
        if (prev[idx]?.is_primary && next.length) next[0] = { ...next[0], is_primary: true };
        return next;
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Remove failed.');
    }
  }, [recordId, endpointBase, setPhotos, disabled]);

  const handleSetPrimary = useCallback(async (idx) => {
    if (!recordId || disabled) return;
    try {
      await axios.patch(`${API}${endpointBase}/${recordId}/photos/${idx}/primary`, {},
        { headers: { Authorization: `Bearer ${token()}` } });
      setPhotos(prev => prev.map((p, i) => ({ ...p, is_primary: i === idx })));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Set primary failed.');
    }
  }, [recordId, endpointBase, setPhotos, disabled]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-3 p-4" data-testid={`${testidPrefix}-section`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold text-gray-800">{title}</p>
        <span className="text-[10px] font-semibold uppercase text-gray-500">{photos.length}/{maxPhotos}</span>
      </div>

      <input
        ref={cameraInputRef} type="file" accept="image/*" capture="environment"
        className="hidden" data-testid={`${testidPrefix}-camera-input`}
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
      />
      <input
        ref={galleryInputRef} type="file" accept="image/*"
        className="hidden" data-testid={`${testidPrefix}-gallery-input`}
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
      />

      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {photos.map((p, idx) => (
            <div
              key={`${p.url}-${idx}`}
              className="relative aspect-square rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-50"
              data-testid={`${testidPrefix}-thumb-${idx}`}
            >
              <img src={photoSrc(p.url)} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
              <button
                onClick={() => !p.is_primary && handleSetPrimary(idx)}
                disabled={disabled}
                className={`absolute top-1 left-1 p-1 rounded-full shadow ${p.is_primary ? 'bg-amber-400' : 'bg-white/80 hover:bg-amber-100'}`}
                data-testid={`${testidPrefix}-primary-${idx}`}
                title={p.is_primary ? 'Primary photo' : 'Set as primary'}
              >
                <Star className={`w-3 h-3 ${p.is_primary ? 'text-white fill-white' : 'text-gray-500'}`} />
              </button>
              {!disabled && (
                <button
                  onClick={() => handleRemove(idx)}
                  className="absolute top-1 right-1 p-1 rounded-full bg-white/90 hover:bg-red-100 shadow"
                  data-testid={`${testidPrefix}-remove-${idx}`}
                  title="Remove photo"
                >
                  <X className="w-3 h-3 text-red-600" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!disabled && photos.length < maxPhotos && (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testidPrefix}-take-btn`}
            className="py-3 rounded-xl border-2 border-emerald-200 bg-emerald-50 text-emerald-700 font-bold text-sm flex items-center justify-center gap-2 hover:bg-emerald-100 active:bg-emerald-200 disabled:opacity-50"
          ><Camera className="w-4 h-4" /> Take Photo</button>
          <button
            onClick={() => galleryInputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testidPrefix}-choose-btn`}
            className="py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-700 font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50"
          ><ImagePlus className="w-4 h-4" /> Choose Photo</button>
        </div>
      )}

      {uploading && (
        <p className="mt-2 text-xs text-emerald-600 font-medium" data-testid={`${testidPrefix}-uploading`}>Uploading…</p>
      )}
      {error && (
        <p className="mt-2 text-xs text-red-600 font-medium" data-testid={`${testidPrefix}-error`}>{error}</p>
      )}
      {photos.length === 0 && !uploading && (
        <p className="mt-3 text-[11px] text-gray-500">{emptyHint}</p>
      )}
    </div>
  );
}
